<script setup lang="ts">
import { useLayout } from '@/layout/composables/layout';
import {useRoute} from "vue-router";
import {computed,} from "vue";
import {AuthenticationService} from "@/service/AuthenticationService.ts";
import {useMonitoring} from "@/composables/useMonitoring";
import router from "@/router";

const authService = new AuthenticationService();
const { trackUserLogout } = useMonitoring();

const { onMenuToggle, layoutState } = useLayout();

const menuClass = computed(() => {
  return {
    'hidden': layoutState.staticMenuMobileActive,
  };
});

const route = useRoute();

const menuItems = computed( () => {
  return [
    {route: '/#how-it-works', label: 'How It Works'},
    {route: '/#pricing', label: 'Pricing'},
    {route: '/#faq', label: 'FAQ'},
    {route: '/#about-us', label: 'About Us'},
    {route: '/#contact-us', label: 'Contact Us'},
    {route: 'https://themailroom.us/', label: 'TheMailroom.US'},
  ].map(entry => {
    return {
      ...entry,
      active: route.name === entry.route
    }
  })
})

async function logout() {
  // Track logout before clearing auth
  trackUserLogout();

  authService.logout();
  await router.push({force: true, path: '/'});
}

</script>

<template>
    <nav id="site-topbar" class="bg-white dark:bg-gray-900 fixed w-full z-20 top-0 start-0 border-b border-gray-200 dark:border-gray-600">
      <div class="max-w-screen-xl flex flex-wrap items-center justify-between mx-auto p-4">
        <a href="/" class="flex items-center space-x-3 rtl:space-x-reverse">
          <img src="/YourMailroom.US-horizontal.png" class="h-7 md:h-10" alt="YourMailroom.US Logo">
        </a>
        
        <div class="flex md:order-2 space-x-1 md:space-x-3 rtl:space-x-reverse">
          <template v-if="authService.getCustomer()">
            <Button icon="pi pi-home" aria-label="My Mailbox" 
                    rounded severity="secondary" variant="text" 
                    class="!bg-transparent hover:!bg-primary-50 !border-0"
                    v-tooltip.bottom="'My Mailbox'"
                    as="router-link" :to="{name:'MyMailbox'}"
            />
            <Button icon="pi pi-sign-out" aria-label="Log Out"
                    rounded severity="secondary" variant="text"
                    class="!bg-transparent hover:!bg-primary-50 !border-0"
                    v-tooltip.bottom="'Log Out'"
                    @click="logout"
            />
          </template>
          <template v-else>
            <Button size="small" as="router-link" :to="{name:'SignUp'}" class="mr-1 md:mr-0 !bg-primary-700">Sign Up</Button>
            <Button size="small" as="router-link" severity="secondary" :to="{name:'Login'}">Login</Button>  
          </template>
          <button data-collapse-toggle="navbar-sticky" type="button" class="inline-flex items-center p-2 w-10 h-10 justify-center text-sm text-gray-500 rounded-lg md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600" aria-controls="navbar-sticky" aria-expanded="false" @click="onMenuToggle">
            <span class="sr-only">Open main menu</span>
            <svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 17 14">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 1h15M1 7h15M1 13h15"/>
            </svg>
          </button>
        </div>
        
        <div class="items-center justify-between w-full md:flex md:w-auto md:order-1" id="navbar-sticky" :class="menuClass">
          <ul class="flex flex-col p-4 md:p-0 mt-4 font-medium border border-gray-100 rounded-lg bg-gray-50 md:space-x-8 rtl:space-x-reverse md:flex-row md:mt-0 md:border-0 md:bg-white dark:bg-gray-800 md:dark:bg-gray-900 dark:border-gray-700">
            <li v-for="menuItem in menuItems" :key="menuItem.route">
              <a
                  v-if="menuItem.route.startsWith('http')"
                  :href="menuItem.route"
                  class="block py-2 px-3 rounded"
                  :class="[
                      menuItem.active ? 'text-white bg-primary-500 md:bg-transparent md:text-primary-500 md:p-0 md:dark:text-primary-500' 
                      : 'text-gray-900 hover:bg-gray-100 md:hover:bg-transparent md:hover:text-primary-500 md:p-0 md:dark:hover:text-primary-500 dark:text-white dark:hover:bg-gray-700 dark:hover:text-white md:dark:hover:bg-transparent dark:border-gray-700'
                  ]"
                  @click="onMenuToggle"
              >
                {{menuItem.label}}
              </a>
              <RouterLink 
                  v-else
                  :to="menuItem.route"
                  class="block py-2 px-3 rounded"
                  :class="[
                      menuItem.active ? 'text-white bg-primary-500 md:bg-transparent md:text-primary-500 md:p-0 md:dark:text-primary-500' 
                      : 'text-gray-900 hover:bg-gray-100 md:hover:bg-transparent md:hover:text-primary-500 md:p-0 md:dark:hover:text-primary-500 dark:text-white dark:hover:bg-gray-700 dark:hover:text-white md:dark:hover:bg-transparent dark:border-gray-700'
                  ]"
                  @click="onMenuToggle"
              >
                {{menuItem.label}}
              </RouterLink>
            </li>
          </ul>
        </div>
      </div>
    </nav>
</template>
